import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  CatalogueConnectionTestResult,
  CataloguePullResult,
  CatalogueReadTestResult,
  CatalogueSyncSessionSummary,
  PullRowStatus,
  SpreadsheetConnectionStatus,
  SyncPreviewProductChange,
  SyncPreviewResult,
} from '@shared';
import { api, ApiClientError } from '../lib/api';
import { FilterChip, SyncBadge } from '../components/ui';
import { Eye, History, PlugZap, RefreshCw, BookOpenCheck } from 'lucide-react';
import { useUserProfile } from '../hooks/useUserProfile';
import { MAX_DISPLAY_NAME_LENGTH } from '../lib/userProfile';
import { updateProfileFromSubmission } from '../lib/profileExperience';
import { legacyServerToolsEnabled } from '../lib/runtimeFeatures';
import { DataStorageSettings } from '../components/DataStorageSettings';
import { AppUpdatesSettings } from '../components/AppUpdatesSettings';
import { AdminAccessSettings } from '../components/AdminAccessSettings';

type FilterKey = 'all' | PullRowStatus;

const FILTERS: Array<{ key: FilterKey; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'changed', label: 'Changed' },
  { key: 'unchanged', label: 'Unchanged' },
  { key: 'invalid', label: 'Invalid' },
  { key: 'conflict', label: 'Conflict' },
  { key: 'archived_upstream', label: 'Archived upstream' },
  { key: 'missing_upstream', label: 'Missing upstream' },
];

function formatVal(v: string | number | boolean | null | undefined): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return String(v);
}

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function Settings(): JSX.Element {
  const legacyToolsEnabled = legacyServerToolsEnabled();
  const { profile, updateDisplayName, refreshProfile } = useUserProfile();
  const [profileName, setProfileName] = useState(profile?.displayName ?? '');
  const [profileMessage, setProfileMessage] = useState<string | null>(null);
  const [status, setStatus] = useState<SpreadsheetConnectionStatus | null>(
    null,
  );
  const [preview, setPreview] = useState<SyncPreviewResult | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<FilterKey>('all');
  const [detailId, setDetailId] = useState<string | null>(null);
  const [history, setHistory] = useState<CatalogueSyncSessionSummary[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [pullResult, setPullResult] = useState<CataloguePullResult | null>(
    null,
  );
  const [connectionTest, setConnectionTest] =
    useState<CatalogueConnectionTestResult | null>(null);
  const [readTest, setReadTest] = useState<CatalogueReadTestResult | null>(
    null,
  );
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [testingConnection, setTestingConnection] = useState(false);
  const [testingRead, setTestingRead] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pullRefreshWarning, setPullRefreshWarning] = useState<string | null>(
    null,
  );

  const syncBusy =
    loadingStatus ||
    testingConnection ||
    testingRead ||
    previewing ||
    pulling;

  const refreshStatus = useCallback(async () => {
    setLoadingStatus(true);
    setError(null);
    try {
      const [next, hist] = await Promise.all([
        api.get<SpreadsheetConnectionStatus>('/catalogue-sync/status'),
        api.get<CatalogueSyncSessionSummary[]>('/catalogue-sync/history'),
      ]);
      setStatus(next);
      setHistory(hist);
    } catch (err) {
      setStatus(null);
      setError(
        err instanceof ApiClientError
          ? err.message
          : 'Failed to load spreadsheet status',
      );
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  useEffect(() => {
    if (!legacyToolsEnabled) {
      setLoadingStatus(false);
      return;
    }
    void refreshStatus();
  }, [legacyToolsEnabled, refreshStatus]);

  const runTestConnection = async (): Promise<void> => {
    setTestingConnection(true);
    setError(null);
    setConnectionTest(null);
    try {
      const result = await api.post<CatalogueConnectionTestResult>(
        '/catalogue-sync/test-connection',
      );
      setConnectionTest(result);
      await refreshStatus();
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.message
          : 'Connection test failed',
      );
    } finally {
      setTestingConnection(false);
    }
  };

  const runTestRead = async (): Promise<void> => {
    setTestingRead(true);
    setError(null);
    setReadTest(null);
    try {
      const result = await api.post<CatalogueReadTestResult>(
        '/catalogue-sync/test-read',
      );
      setReadTest(result);
      await refreshStatus();
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.message
          : 'Catalogue read test failed',
      );
    } finally {
      setTestingRead(false);
    }
  };

  const runPreview = async (): Promise<void> => {
    setPreviewing(true);
    setError(null);
    setPullResult(null);
    setPullRefreshWarning(null);
    try {
      const result = await api.post<SyncPreviewResult>(
        '/catalogue-sync/preview',
      );
      setPreview(result);
      setSelected(new Set());
      setDetailId(null);
      await refreshStatus();
    } catch (err) {
      setError(
        err instanceof ApiClientError ? err.message : 'Preview failed',
      );
    } finally {
      setPreviewing(false);
    }
  };

  const filtered = useMemo(() => {
    if (!preview) return [];
    if (filter === 'all') return preview.products;
    return preview.products.filter((p) => p.status === filter);
  }, [preview, filter]);

  const toggle = (productId: string, row: SyncPreviewProductChange): void => {
    if (!row.selectable) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const selectAllValidNew = (): void => {
    if (!preview) return;
    setSelected(
      new Set(
        preview.products
          .filter((p) => p.status === 'new' && p.selectable)
          .map((p) => p.productId),
      ),
    );
  };

  const selectAllChanged = (): void => {
    if (!preview) return;
    setSelected(
      new Set(
        preview.products
          .filter(
            (p) =>
              (p.status === 'changed' ||
                p.status === 'conflict' ||
                p.status === 'archived_upstream') &&
              p.selectable,
          )
          .map((p) => p.productId),
      ),
    );
  };

  const applyPull = async (): Promise<void> => {
    if (!preview || selected.size === 0) return;
    setPulling(true);
    setError(null);
    setPullRefreshWarning(null);
    try {
      const result = await api.post<CataloguePullResult>(
        '/catalogue-sync/pull',
        {
          previewId: preview.previewId,
          productIds: [...selected],
        },
      );
      setPullResult(result);
      setSelected(new Set());
      try {
        const nextPreview = await api.post<SyncPreviewResult>(
          '/catalogue-sync/preview',
        );
        setPreview(nextPreview);
        await refreshStatus();
      } catch {
        setPullRefreshWarning(
          'Pull succeeded, but refreshing the preview failed. Open Catalogue to confirm products, or run Preview again.',
        );
        try {
          await refreshStatus();
        } catch {
          /* status refresh is best-effort after a successful pull */
        }
      }
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Pull failed');
    } finally {
      setPulling(false);
    }
  };

  const detail = preview?.products.find((p) => p.productId === detailId);
  const isDev = import.meta.env.DEV;
  const previewWarningCount = useMemo(() => {
    if (!preview) return 0;
    return preview.products.reduce((n, p) => n + p.warnings.length, 0);
  }, [preview]);

  const saveProfile = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setProfileMessage(null);
    try {
      updateProfileFromSubmission(profileName, updateDisplayName);
      setProfileMessage('Profile saved.');
    } catch (profileError) {
      setProfileMessage(
        profileError instanceof Error ? profileError.message : 'Profile could not be saved.',
      );
    }
  };

  return (
    <div className="stack">
      <h1 className="page-title">Settings</h1>
      <p className="page-lead">Connection, sync, and device health.</p>

      <section className="card stack settings-grouped" aria-labelledby="profile-settings-title">
        <h2 id="profile-settings-title" className="mt-0">Profile</h2>
        <p className="page-lead mt-0">Stored locally on this device.</p>
        <form className="profile-settings-form" onSubmit={saveProfile}>
          <label htmlFor="settings-profile-name">Name</label>
          <input
            id="settings-profile-name"
            type="text"
            autoComplete="name"
            maxLength={MAX_DISPLAY_NAME_LENGTH}
            value={profileName}
            onChange={(event) => {
              setProfileName(event.target.value);
              setProfileMessage(null);
            }}
          />
          {profileMessage ? <p className="hint" role="status">{profileMessage}</p> : null}
          <button type="submit" className="btn btn-primary" disabled={!profileName.trim()}>
            Save
          </button>
        </form>
      </section>

      <DataStorageSettings onRestoreComplete={refreshProfile} />
      <AppUpdatesSettings />
      <AdminAccessSettings />

      {legacyToolsEnabled ? (
        <section className="card stack settings-grouped">
          <h2 className="mt-0">Spreadsheet connection</h2>
        <p className="page-lead" style={{ marginTop: 0 }}>
          Read-only catalogue via Google Apps Script. Test the connection, then
          preview and approve a pull — nothing writes to the sheet, and nothing
          updates local products until you confirm.
        </p>

        {loadingStatus && !status ? (
          <p className="muted">Checking connection…</p>
        ) : status ? (
          <dl className="settings-dl">
            <div>
              <dt>Provider</dt>
              <dd>{status.providerLabel ?? status.provider}</dd>
            </div>
            <div>
              <dt>Connection status</dt>
              <dd>
                {status.state === 'connected' || status.state === 'fixture'
                  ? 'Connected'
                  : status.state === 'configured'
                    ? 'Configured'
                    : 'Unavailable'}
                <span className="muted"> ({status.state})</span>
              </dd>
            </div>
            <div>
              <dt>Spreadsheet name</dt>
              <dd>{status.spreadsheetName ?? '—'}</dd>
            </div>
            <div>
              <dt>Apps Script endpoint host</dt>
              <dd className="mono">{status.endpointHost ?? '—'}</dd>
            </div>
            <div>
              <dt>Last successful status check</dt>
              <dd>{formatWhen(status.lastStatusCheckAt)}</dd>
            </div>
            <div>
              <dt>Last successful catalogue read</dt>
              <dd>{formatWhen(status.lastReadAt)}</dd>
            </div>
            <div>
              <dt>Products row count</dt>
              <dd>
                {status.productsRowCount != null ? status.productsRowCount : '—'}
              </dd>
            </div>
            <div>
              <dt>Optional sheet availability</dt>
              <dd>
                {status.sheetTitles && status.sheetTitles.length > 0
                  ? status.sheetTitles.join(', ')
                  : '—'}
              </dd>
            </div>
            <div>
              <dt>Validation warnings</dt>
              <dd>
                {status.validationWarnings &&
                status.validationWarnings.length > 0 ? (
                  <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
                    {status.validationWarnings.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </ul>
                ) : (
                  'None'
                )}
              </dd>
            </div>
            <div>
              <dt>Last preview</dt>
              <dd>{formatWhen(status.lastPreviewAt)}</dd>
            </div>
            <div>
              <dt>Last approved pull</dt>
              <dd>{formatWhen(status.lastPullAt)}</dd>
            </div>
            <div>
              <dt>Last sync error</dt>
              <dd>{status.lastError ?? '—'}</dd>
            </div>
            <div>
              <dt>Token configured</dt>
              <dd>
                {status.tokenConfigured == null
                  ? '—'
                  : status.tokenConfigured
                    ? 'Yes'
                    : 'No'}
              </dd>
            </div>
            <div>
              <dt>Message</dt>
              <dd>{status.message}</dd>
            </div>
          </dl>
        ) : null}

        <div className="row" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={syncBusy}
            onClick={() => void runTestConnection()}
          >
            <PlugZap size={18} aria-hidden />
            {testingConnection ? 'Testing…' : 'Test Connection'}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={syncBusy}
            onClick={() => void runTestRead()}
          >
            <BookOpenCheck size={18} aria-hidden />
            {testingRead ? 'Reading…' : 'Test Catalogue Read'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={syncBusy}
            onClick={() => void runPreview()}
          >
            <Eye size={18} aria-hidden />
            {previewing ? 'Previewing…' : 'Preview Spreadsheet Changes'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={syncBusy}
            onClick={() => {
              setShowHistory(true);
              void refreshStatus();
            }}
          >
            <History size={18} aria-hidden />
            View Sync History
          </button>
          <button
            type="button"
            className="btn"
            disabled={syncBusy}
            onClick={() => void refreshStatus()}
          >
            <RefreshCw size={18} aria-hidden />
            Refresh
          </button>
        </div>

        {error ? <p className="error-text">{error}</p> : null}

        {connectionTest ? (
          <div
            className={`notice${connectionTest.ok ? '' : ' notice--error'}`}
            role="status"
          >
            <strong>
              {connectionTest.ok
                ? 'Connection OK'
                : `Connection failed (${connectionTest.issues[0]?.code ?? 'error'})`}
            </strong>
            <p style={{ margin: '0.35rem 0 0' }}>{connectionTest.message}</p>
            {connectionTest.spreadsheetName ? (
              <p className="muted" style={{ margin: '0.35rem 0 0' }}>
                Spreadsheet: {connectionTest.spreadsheetName}
              </p>
            ) : null}
            {isDev && connectionTest.developerDetail ? (
              <details style={{ marginTop: '0.5rem' }}>
                <summary>Developer details</summary>
                <pre className="formula">{connectionTest.developerDetail}</pre>
              </details>
            ) : null}
          </div>
        ) : null}

        {readTest ? (
          <div
            className={`notice${readTest.ok ? '' : ' notice--error'}`}
            role="status"
          >
            <strong>
              {readTest.ok ? 'Catalogue read OK' : 'Catalogue read failed'}
            </strong>
            <p style={{ margin: '0.35rem 0 0' }}>{readTest.message}</p>
            <ul className="muted" style={{ marginBottom: 0 }}>
              <li>Products rows: {readTest.productsRowCount}</li>
              <li>
                Headers:{' '}
                {readTest.productHeaders.length > 0
                  ? readTest.productHeaders.join(' · ')
                  : '—'}
              </li>
              <li>
                Optional sheets:{' '}
                {readTest.optionalSheets
                  .map(
                    (s) =>
                      `${s.name}${s.present ? ` (${s.rowCount})` : ' (missing)'}`,
                  )
                  .join(', ')}
              </li>
              <li>
                Duplicate Product IDs:{' '}
                {readTest.duplicateProductIds.length > 0
                  ? readTest.duplicateProductIds.join(', ')
                  : 'none'}
              </li>
              <li>Blank Product ID rows: {readTest.blankProductIdRowCount}</li>
              <li>
                Percentage warnings:{' '}
                {readTest.percentageWarnings.length > 0
                  ? readTest.percentageWarnings.join('; ')
                  : 'none'}
              </li>
            </ul>
            {isDev && readTest.developerDetail ? (
              <details style={{ marginTop: '0.5rem' }}>
                <summary>Developer details</summary>
                <pre className="formula">{readTest.developerDetail}</pre>
              </details>
            ) : null}
          </div>
        ) : null}

        {pullResult ? (
          <div className="card stack" role="status">
            <h3 className="mt-0">Pull applied</h3>
            <p className="muted" style={{ marginTop: 0 }}>
              Session {pullResult.sessionId.slice(0, 8)}… ·{' '}
              {pullResult.createdCount} created, {pullResult.updatedCount}{' '}
              updated, {pullResult.archivedCount} archived,{' '}
              {pullResult.skippedCount} skipped
              {pullResult.invalidCount > 0
                ? `, ${pullResult.invalidCount} invalid`
                : ''}
              .
            </p>
            {pullResult.warnings.length > 0 ? (
              <ul className="hint" style={{ marginBottom: 0 }}>
                {pullResult.warnings.slice(0, 8).map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            ) : null}
            {pullResult.errors.length > 0 ? (
              <ul className="error-text" style={{ marginBottom: 0 }}>
                {pullResult.errors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            ) : null}
            {pullRefreshWarning ? (
              <p className="hint" style={{ marginBottom: 0 }}>
                {pullRefreshWarning}
              </p>
            ) : null}
            <p style={{ marginBottom: 0 }}>
              <Link to="/catalogue" className="btn btn-secondary">
                Open Catalogue
              </Link>
            </p>
          </div>
        ) : null}

        {preview ? (
          <div className="stack">
            <h3 className="mt-0">Review products</h3>
            <p className="muted">
              Preview {preview.previewId.slice(0, 8)}… ·{' '}
              {preview.workbookTitle ?? preview.spreadsheetId} ·{' '}
              {preview.canPullLater
                ? 'Validation OK'
                : 'Validation has errors — invalid rows blocked'}
            </p>
            <ul className="muted">
              <li>New: {preview.summary.newCount}</li>
              <li>Changed: {preview.summary.changedCount}</li>
              <li>Unchanged: {preview.summary.unchangedCount}</li>
              <li>Invalid / skipped: {preview.summary.invalidCount}</li>
              <li>Conflict: {preview.summary.conflictCount}</li>
              <li>Archived upstream: {preview.summary.archivedUpstreamCount}</li>
              <li>Missing upstream: {preview.summary.missingUpstreamCount}</li>
              <li>Row warnings: {previewWarningCount}</li>
            </ul>

            <div className="filter-chips" role="group" aria-label="Sync filters">
              {FILTERS.map((f) => (
                <FilterChip
                  key={f.key}
                  label={f.label}
                  active={filter === f.key}
                  onClick={() => setFilter(f.key)}
                />
              ))}
            </div>

            <div className="row">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={selectAllValidNew}
              >
                Select all valid new
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={selectAllChanged}
              >
                Select all changed
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setSelected(new Set())}
              >
                Clear selection
              </button>
            </div>

            <div className="sync-review-list">
              {filtered.map((p) => {
                const primary =
                  p.diffs[0] ??
                  p.fields.find((f) => f.field === 'thcPercent') ??
                  p.fields[0];
                return (
                  <div
                    key={p.productId}
                    className={`sync-review-row sync-review-row--${p.status}`}
                  >
                    <div className="spread" style={{ alignItems: 'flex-start' }}>
                      <label className="row" style={{ gap: '0.65rem' }}>
                        <input
                          type="checkbox"
                          disabled={!p.selectable}
                          checked={selected.has(p.productId)}
                          onChange={() => toggle(p.productId, p)}
                          aria-label={`Select ${p.productId}`}
                        />
                        <div>
                          <div style={{ fontWeight: 700 }}>
                            {p.displayName ?? 'Untitled'}
                          </div>
                          <div className="mono product-card__id">
                            {p.productId}
                          </div>
                        </div>
                      </label>
                      <SyncBadge status={p.status} />
                    </div>
                    <div
                      className="detail-grid"
                      style={{ marginTop: '0.75rem' }}
                    >
                      <div>
                        <div className="muted">Spreadsheet</div>
                        <div className="mono">
                          {formatVal(primary?.spreadsheetValue)}
                        </div>
                      </div>
                      <div>
                        <div className="muted">Local</div>
                        <div className="mono">
                          {formatVal(primary?.localValue)}
                        </div>
                      </div>
                      <div>
                        <div className="muted">Proposed</div>
                        <div className="mono">
                          {formatVal(primary?.proposedValue)}
                        </div>
                      </div>
                    </div>
                    {p.warnings.length > 0 ? (
                      <p className="hint" style={{ marginBottom: 0 }}>
                        {p.warnings.slice(0, 2).join('; ')}
                      </p>
                    ) : null}
                    <button
                      type="button"
                      className="btn btn-secondary"
                      style={{ marginTop: '0.65rem' }}
                      onClick={() => setDetailId(p.productId)}
                    >
                      Expand differences
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="sync-sticky-actions">
              <button
                type="button"
                className="btn btn-primary btn-block"
                disabled={syncBusy || selected.size === 0}
                onClick={() => void applyPull()}
              >
                {pulling
                  ? 'Applying…'
                  : `Apply Approved Pull (${selected.size})`}
              </button>
            </div>

            {detail ? (
              <div className="card stack">
                <h4 className="mt-0">Row details</h4>
                <p className="mono">{detail.productId}</p>
                <p className="muted">
                  Status: {detail.status}
                  {detail.sheetRowNumber
                    ? ` · sheet row ${detail.sheetRowNumber}`
                    : ''}
                </p>
                {detail.warnings.length > 0 ? (
                  <ul>
                    {detail.warnings.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </ul>
                ) : null}
                <div className="table-wrap">
                  <table className="settings-table">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>Spreadsheet</th>
                        <th>Local</th>
                        <th>Proposed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.fields.map((f) => (
                        <tr key={f.field}>
                          <td>{f.field}</td>
                          <td className="mono">
                            {formatVal(f.spreadsheetValue)}
                          </td>
                          <td className="mono">{formatVal(f.localValue)}</td>
                          <td className="mono">
                            {formatVal(f.proposedValue)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button
                  type="button"
                  className="btn"
                  onClick={() => setDetailId(null)}
                >
                  Close details
                </button>
              </div>
            ) : null}

            {preview.validation.issues.length > 0 ? (
              <div>
                <h4>Validation issues</h4>
                <ul>
                  {preview.validation.issues.slice(0, 20).map((issue, i) => (
                    <li key={`${issue.code}-${i}`}>
                      [{issue.severity}] {issue.sheet}: {issue.message}
                      {issue.productId ? (
                        <span className="mono"> ({issue.productId})</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}
        </section>
      ) : null}

      {legacyToolsEnabled && (showHistory || history.length > 0) && (
        <section className="card stack">
          <div className="spread">
            <h2 className="mt-0">Sync history</h2>
            <button
              type="button"
              className="btn"
              onClick={() => setShowHistory(false)}
            >
              Hide
            </button>
          </div>
          {history.length === 0 ? (
            <p className="muted mt-0">No pull sessions yet.</p>
          ) : (
            <div className="table-wrap">
              <table className="settings-table">
                <thead>
                  <tr>
                    <th>Started</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Updated</th>
                    <th>Archived</th>
                    <th>Session</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id}>
                      <td>{formatWhen(h.startedAt)}</td>
                      <td>{h.status}</td>
                      <td>{h.createdCount}</td>
                      <td>{h.updatedCount}</td>
                      <td>{h.archivedCount}</td>
                      <td className="mono">{h.id.slice(0, 8)}…</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
